
import { RhythmicSimulationState, Ball, SimulationConfig, Vector2D, MorphData, BallPiece } from '@/types';
import { CoreSimulationLogic } from './CoreSimulationLogic';
import { ShapeUtils } from './physics/ShapeUtils';
import { BallPool } from '@/services/BallPool';

export class RhythmicMultiSimLogic {
  public static updateAll(
    simStates: RhythmicSimulationState[],
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    simTime: number,
    rotRad: number,
    hue: number,
    morphData: MorphData,
    collisionEventsRef: React.MutableRefObject<any[]>,
    persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
    persistentSpectrumTargetRef: React.MutableRefObject<Float32Array>,
    shakeRef: React.MutableRefObject<number>,
    triggerBlast: any,
    rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
    rhythmicResetTimeoutRef: React.MutableRefObject<any>,
    ballPool: BallPool,
    trailCircles: any[],
    trailCirclePool: any,
    setBounceCount: any
  ) {
      // 1. Arrange Layout
      this.arrangeLayout(simStates, effectiveCfg);

      // 2. Update each active simulation
      simStates.filter(s => s.active).forEach(sim => {
          if (!sim.ball) return;
          
          const localRefs = this.createLocalRefs(sim);
          const mergedCfg = { ...effectiveCfg, ...sim.settings };
          const localCurrentTS = sim.rhythmicTimeScale * effectiveCfg.masterSpeed;

          // Time-based acceleration for each sub-simulation
          if (mergedCfg.rhythmicTimeScaleIncreaseMode === 'TIME') {
              sim.rhythmicTimeScale += (sim.rhythmicTimeScaleRate) * (deltaTime / 1000);
              if (mergedCfg.rhythmicTimeScaleAcceleration !== 0) {
                  sim.rhythmicTimeScaleRate += mergedCfg.rhythmicTimeScaleAcceleration * (deltaTime / 1000);
              }
          }

          CoreSimulationLogic.RhythmicLogic.update(
              sim.ball,
              mergedCfg,
              deltaTime,
              sim.center.x,
              sim.center.y,
              localRefs.radius,
              localRefs.growthRate,
              localRefs.timeScale,
              localRefs.timeScaleRate,
              rotRad,
              hue,
              morphData,
              collisionEventsRef,
              persistentSpectrumBoostRef,
              persistentSpectrumTargetRef,
              simTime,
              localCurrentTS,
              (intensity) => { shakeRef.current = Math.max(shakeRef.current, intensity); },
              triggerBlast,
              rhythmicBreakPiecesRef,
              rhythmicResetTimeoutRef,
              localRefs.index,
              () => {
                  const balls = CoreSimulationLogic.RhythmicLogic.init(
                      mergedCfg, 
                      localRefs.index,
                      ballPool.get.bind(ballPool) as any,
                      sim.center.x, 
                      sim.center.y, 
                      () => ({ pos: { x: 0, y: 0 }, vel: { x: 0, y: 0 } })
                  );
                  sim.ball = balls[0];
              },
              () => setBounceCount((prev: number) => prev + 1),
              undefined,
              { filtered: sim.trackIndices, background: sim.backgroundTrackIndices, muted: sim.mutedTrackIndices }
          );

          this.enforceBoundaries(sim, mergedCfg, rotRad, morphData);
          
          if (mergedCfg.trailEnabled || mergedCfg.enableTrails || mergedCfg.trailLength > 0 || mergedCfg.trailPersistence) {
              CoreSimulationLogic.TrailLogic.updateTrail(sim.ball, mergedCfg, deltaTime, 1, localCurrentTS, trailCircles, hue, trailCirclePool, simStates.length);
          }
      });
  }

  private static arrangeLayout(simStates: RhythmicSimulationState[], config: SimulationConfig) {
      const count = simStates.length;
      if (count === 0) return;

      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);
      const width = window.innerWidth * 0.9;
      const height = window.innerHeight * 0.8;
      const cellWidth = width / cols;
      const cellHeight = height / rows;
      const maxAllowedRadius = Math.min(cellWidth, cellHeight) * 0.45;

      simStates.forEach((sim, i) => {
          const simSettings = sim.settings || {};
          const col = i % cols;
          const row = Math.floor(i / cols);
          
          let xOffset = 0;
          if (row === rows - 1) {
              const itemsInLastRow = count - ((rows - 1) * cols);
              xOffset = (width - (itemsInLastRow * cellWidth)) / 2;
          }

          const targetX = (col * cellWidth) + (cellWidth / 2) - (width / 2) + xOffset;
          const targetY = (row * cellHeight) + (cellHeight / 2) - (height / 2);

          sim.targetCenter = simSettings.rhythmicSimCenter ? { ...simSettings.rhythmicSimCenter } : { x: targetX, y: targetY };
          
          const lerpFactor = 0.08;
          const dx = (sim.targetCenter.x - sim.center.x) * lerpFactor;
          const dy = (sim.targetCenter.y - sim.center.y) * lerpFactor;
          sim.center.x += dx;
          sim.center.y += dy;
          if (sim.ball) {
              sim.ball.pos.x += dx;
              sim.ball.pos.y += dy;
          }

          const targetRadius = Math.min(simSettings.containerRadius || config.containerRadius, maxAllowedRadius);
          sim.containerRadius += (targetRadius - sim.containerRadius) * lerpFactor;
      });
  }

  private static enforceBoundaries(sim: RhythmicSimulationState, config: SimulationConfig, rotRad: number, morphData: MorphData) {
      if (!sim.ball) return;
      const relX = sim.ball.pos.x - sim.center.x;
      const relY = sim.ball.pos.y - sim.center.y;
      const dist = Math.sqrt(relX * relX + relY * relY);
      const angle = Math.atan2(relY, relX);
      const lineWidth = config.containerLineWidth || 4;
      const baseR = sim.containerRadius;
      const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, baseR, morphData.baseRadius) - lineWidth / 2;
      
      if (dist + sim.ball.radius > effectiveShapeR) {
          const overlap = (dist + sim.ball.radius) - effectiveShapeR;
          const localPos = {
              x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad),
              y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad)
          };
          const localNormal = ShapeUtils.getNormal(localPos, morphData.from, baseR - lineWidth / 2, morphData.to, morphData.factor, morphData.baseRadius);
          const worldNormal = {
              x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad),
              y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad)
          };
          sim.ball.pos.x -= worldNormal.x * (overlap + 0.5);
          sim.ball.pos.y -= worldNormal.y * (overlap + 0.5);
      }
  }

  private static createLocalRefs(sim: RhythmicSimulationState) {
      return {
          radius: { get current() { return sim.containerRadius; }, set current(v: number) { sim.containerRadius = v; } },
          growthRate: { get current() { return sim.ballGrowthRate; }, set current(v: number) { sim.ballGrowthRate = v; } },
          timeScale: { get current() { return sim.rhythmicTimeScale; }, set current(v: number) { sim.rhythmicTimeScale = v; } },
          timeScaleRate: { get current() { return sim.rhythmicTimeScaleRate; }, set current(v: number) { sim.rhythmicTimeScaleRate = v; } },
          timeScaleAcceleration: { get current() { return sim.rhythmicTimeScaleAcceleration; } },
          index: { get current() { return sim.rhythmicIndex; }, set current(v: number) { sim.rhythmicIndex = v; } }
      };
  }
}
